import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { API } from '../lib/api';
import { BookOpen, MagnifyingGlass, CaretRight, X, FilePdf, DownloadSimple, FileMd } from '@phosphor-icons/react';
import { CATEGORIES, allArticles, searchArticles } from '../docs';
import { Block } from '../docs/Block';
import { printArticlePdf, printAllPdf, articleToMarkdown, allToMarkdown, downloadText } from '../lib/docExport';

export default function DocsCenter() {
  const articles = useMemo(() => allArticles(API), []);
  const [params, setParams] = useSearchParams();
  const [roleFilter, setRoleFilter] = useState('all');
  const docRoles = useMemo(() => [...new Set(articles.flatMap((a) => a.roles || []))].sort(), [articles]);
  const visible = useMemo(() => roleFilter === 'all' ? articles : articles.filter((a) => !(a.roles || []).length || a.roles.includes(roleFilter)), [articles, roleFilter]);
  const byCat = useMemo(() => Object.fromEntries(CATEGORIES.map((c) => [c.key, visible.filter((a) => a.cat === c.key)])), [visible]);
  const [activeId, setActiveId] = useState(params.get('doc') || articles[0].id);
  const [q, setQ] = useState('');
  const [lightbox, setLightbox] = useState(null);
  const results = useMemo(() => searchArticles(articles, q), [articles, q]);
  const active = articles.find((a) => a.id === activeId) || articles[0];
  const idx = articles.indexOf(active);
  const open = (id) => { setActiveId(id); setQ(''); setParams({ doc: id }, { replace: true }); window.scrollTo({ top: 0, behavior: 'smooth' }); };

  // Respond to deep links (?doc=) arriving from the in-module Help panel.
  useEffect(() => {
    const d = params.get('doc');
    if (d && articles.some((a) => a.id === d) && d !== activeId) { setActiveId(d); window.scrollTo({ top: 0 }); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const dlBtn = 'flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border border-slate-200 bg-white hover:border-orange-400 hover:text-orange-600 transition-colors';

  return (
    <div className="space-y-5" data-testid="docs-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-extrabold tracking-tight flex items-center gap-2"><BookOpen size={26} className="text-orange-600" />Documentation</h1>
          <p className="text-sm text-slate-500">Setup, every module, every workflow - validated against this running platform.</p>
        </div>
        <div className="flex gap-2 w-full sm:w-auto items-center flex-wrap">
          <button data-testid="docs-download-all-pdf" onClick={() => printAllPdf(articles, CATEGORIES)} className={dlBtn}>
            <FilePdf size={14} /> Full docs (PDF)
          </button>
          <button data-testid="docs-download-all-md" onClick={() => downloadText('ONRID-documentation.md', allToMarkdown(articles, CATEGORIES))} className={dlBtn}>
            <FileMd size={14} /> Markdown
          </button>
          <select data-testid="docs-role-filter" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}
            className="border border-slate-200 rounded-md px-2 py-2 text-sm bg-white">
            <option value="all">All roles</option>
            {docRoles.map((r) => <option key={r} value={r}>{`For ${r.replace(/_/g, ' ')}s`}</option>)}
          </select>
          <div className="relative flex-1 sm:w-72">
            <MagnifyingGlass size={15} className="absolute start-3 top-2.5 text-slate-400" />
            <input data-testid="docs-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search the docs..."
              className="w-full border border-slate-200 rounded-md ps-8 pe-3 py-2 text-sm focus:border-orange-500 focus:outline-none bg-white" />
          </div>
        </div>
      </div>

      {q.trim() ? (
        <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="docs-search-results">
          <p className="font-mono-label text-[10px] uppercase text-slate-400 mb-2">{results.length} result{results.length === 1 ? '' : 's'} for "{q}"</p>
          {results.map(({ article, snippet }) => (
            <button key={article.id} data-testid={`docs-result-${article.id}`} onClick={() => open(article.id)}
              className="block w-full text-start px-3 py-2.5 rounded-md hover:bg-slate-50 border-b border-slate-50 last:border-0">
              <span className="text-sm font-semibold text-slate-800 flex items-center gap-1">{article.title}<CaretRight size={12} className="text-slate-300" /></span>
              <span className="block text-xs text-slate-500 mt-0.5">...{snippet}...</span>
            </button>))}
          {results.length === 0 && <p className="text-sm text-slate-400 py-4 text-center">Nothing matched - try a module name, an error message or a task like "issue transcript".</p>}
        </div>
      ) : (
        <div className="grid lg:grid-cols-[250px_1fr] gap-5">
          <nav className="space-y-4" data-testid="docs-nav">
            {CATEGORIES.map((c) => (
              <div key={c.key}>
                <p className="font-mono-label text-[10px] uppercase text-slate-400 mb-1 px-2">{c.label}</p>
                {byCat[c.key].map((a) => (
                  <button key={a.id} data-testid={`docs-nav-${a.id}`} onClick={() => open(a.id)}
                    className={`block w-full text-start text-[13px] px-2.5 py-1.5 rounded-md transition-colors ${active.id === a.id ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-white'}`}>{a.title}</button>
                ))}
              </div>))}
          </nav>
          <article className="bg-white border border-slate-200 rounded-xl p-6 min-w-0" data-testid="docs-article">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <p className="font-mono-label text-[10px] uppercase text-slate-400">{CATEGORIES.find((c) => c.key === active.cat)?.label}</p>
                <h2 className="font-display text-xl font-extrabold tracking-tight mt-0.5" data-testid="docs-article-title">{active.title}</h2>
              </div>
              <div className="flex gap-2 shrink-0">
                <button data-testid="docs-download-pdf" onClick={() => printArticlePdf(active)} className={dlBtn}><FilePdf size={14} /> PDF</button>
                <button data-testid="docs-download-md" onClick={() => downloadText(`${active.id}.md`, articleToMarkdown(active))} className={dlBtn}><FileMd size={14} /> MD</button>
              </div>
            </div>
            <p className="text-sm text-slate-500 mt-1 mb-1">{active.summary}</p>
            <div className="flex gap-1.5 flex-wrap mb-3">
              {(active.roles || []).map((r) => <span key={r} className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 font-mono-label uppercase">{r}</span>)}
            </div>
            <div className="border-t border-slate-100 pt-1">
              {active.blocks.map((b, i) => <Block key={i} b={b} onImg={setLightbox} />)}
            </div>
            <div className="flex justify-between mt-8 pt-4 border-t border-slate-100">
              {idx > 0 ? <button data-testid="docs-prev" onClick={() => open(articles[idx - 1].id)} className="text-sm text-slate-500 hover:text-slate-900">&larr; {articles[idx - 1].title}</button> : <span />}
              {idx < articles.length - 1 && <button data-testid="docs-next" onClick={() => open(articles[idx + 1].id)} className="text-sm text-slate-500 hover:text-slate-900 text-end">{articles[idx + 1].title} &rarr;</button>}
            </div>
          </article>
        </div>
      )}

      {lightbox && (
        <div className="fixed inset-0 z-50 bg-slate-900/80 flex items-center justify-center p-6 cursor-zoom-out" data-testid="docs-lightbox" onClick={() => setLightbox(null)}>
          <div className="absolute top-4 end-4 flex items-center gap-3" onClick={(e) => e.stopPropagation()}>
            <a data-testid="docs-lightbox-download" href={lightbox.img} download className="flex items-center gap-1.5 text-white text-sm bg-white/10 hover:bg-white/20 rounded-md px-3 py-1.5 transition-colors">
              <DownloadSimple size={16} /> Download image
            </a>
            <button className="text-white" aria-label="Close" onClick={() => setLightbox(null)}><X size={24} /></button>
          </div>
          <img src={lightbox.img} alt={lightbox.cap || 'screenshot'} className="max-h-full max-w-full rounded-lg shadow-2xl" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
}
